import { useQuery } from '@tanstack/react-query';
import type { CrmBoardQuery, CrmManagerHintDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { cn } from '@upme/ui/lib/utils';
import { RefreshCw, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatEnergyFull } from '../../../shared/format-energy';
import { formatDateTime } from '../../users/lib/format';
import { useDismissCrmHint, useRefreshCrmSuggestions } from '../mutations';
import { crmSuggestionsOptions } from '../queries';
import { CrmHintComposeDialog } from './crm-hint-compose-dialog';

type Props = {
  filters: CrmBoardQuery;
  enabled: boolean;
};

function formatPrice(minor: number | null | undefined, currency: string | null | undefined): string | null {
  if (minor == null || minor <= 0) return null;
  const major = minor / 100;
  return currency ? `${major} ${currency}` : String(major);
}

function HintCard({
  hint,
  onCompose,
  onDismiss,
  dismissing,
}: {
  hint: CrmManagerHintDto;
  onCompose: () => void;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  const kindLabel = hint.kind === 'onboard' ? 'Новичок' : 'Мероприятие';

  return (
    <article className="flex min-w-0 flex-1 flex-col gap-2 rounded-lg border border-border/70 bg-card p-3 shadow-sm">
      <header className="flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          {kindLabel}
        </span>
        <span className="truncate text-sm font-medium">{hint.contactLabel}</span>
      </header>
      <p className="text-sm leading-snug text-foreground">{hint.whyNow}</p>
      {hint.matchReason ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">Почему ивент:</span> {hint.matchReason}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        <span className="font-medium">Предложение:</span> {hint.offerLabel}
      </p>
      {hint.event ? (
        <p className="text-xs text-muted-foreground">
          {formatDateTime(hint.event.startsAt)} ({hint.event.timeZone})
          {formatPrice(hint.event.priceMinor, hint.event.currency)
            ? ` · ${formatPrice(hint.event.priceMinor, hint.event.currency)}`
            : ''}
        </p>
      ) : null}
      {hint.reward && hint.reward.potentialEnergy > 0 ? (
        <p className="text-xs text-muted-foreground">
          {hint.reward.label}: ⚡ {formatEnergyFull(hint.reward.potentialEnergy)} · {hint.reward.condition}
        </p>
      ) : null}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer font-medium text-foreground/80">С чего начать</summary>
        <p className="mt-1 whitespace-pre-wrap">{hint.howToStart}</p>
      </details>
      <div className="mt-auto flex flex-col gap-2">
        <Button type="button" size="sm" className="w-full" onClick={onCompose}>
          Написать сообщение
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="w-full"
          disabled={dismissing}
          onClick={onDismiss}
        >
          Закрыть
        </Button>
      </div>
    </article>
  );
}

export function CrmManagerHintsStrip({ filters, enabled }: Props) {
  const { data, isLoading, isError, isFetching } = useQuery({
    ...crmSuggestionsOptions(filters),
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
  const refresh = useRefreshCrmSuggestions(filters);
  const dismiss = useDismissCrmHint();
  const [composeHint, setComposeHint] = useState<CrmManagerHintDto | null>(null);

  function handleDismiss(hint: CrmManagerHintDto) {
    dismiss.mutate(hint.id, { onError: () => toast.error('Не удалось закрыть подсказку') });
  }

  if (!enabled) return null;

  return (
    <section aria-label="Подсказки для менеджера" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="size-4 text-primary" aria-hidden />
          Подсказки ИИ
          {data?.error === 'llm_failed' ? (
            <span className="text-xs font-normal text-amber-600">(формулировки без ИИ)</span>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending || isFetching}
        >
          <RefreshCw
            className={cn('mr-1 size-4', (refresh.isPending || isFetching) && 'animate-spin')}
            aria-hidden
          />
          Обновить
        </Button>
      </div>
      {isLoading ? <p className="text-sm text-muted-foreground">Собираем подсказки…</p> : null}
      {isError ? <p className="text-sm text-muted-foreground">Не удалось загрузить подсказки.</p> : null}
      {data && data.items.length === 0 && !isLoading ? (
        <p className="text-sm text-muted-foreground">Сейчас нет подходящих подсказок по этим фильтрам.</p>
      ) : null}
      {data && data.items.length > 0 ? (
        <div className="flex flex-col gap-3 lg:flex-row">
          {data.items.map((hint) => (
            <HintCard
              key={hint.id}
              hint={hint}
              onCompose={() => setComposeHint(hint)}
              onDismiss={() => handleDismiss(hint)}
              dismissing={dismiss.isPending && dismiss.variables === hint.id}
            />
          ))}
        </div>
      ) : null}
      <CrmHintComposeDialog
        hint={composeHint}
        open={composeHint != null}
        onOpenChange={(open) => {
          if (!open) setComposeHint(null);
        }}
      />
    </section>
  );
}
